import { defineStore } from 'pinia';
import attributesApi from '@/app/features/ams/attributes/attributes-service';
import {
  AttributeResponse,
  CheckCodeSyntaxPayload,
} from '@/app/shared/types/params/code-meta-information-params.interface';
import {
  PaginationParams,
  SearchParams,
} from '@/app/shared/types/params/request-params.interface';
import { CodeType } from '@/app/shared/enums/code-type.enum';
import { useAlertStore } from '@/app/shared/stores/alert-store';
import { Motif } from '@/app/shared/enums/motif-enum';
import { ValidateCode } from '@/app/features/ams/shared/types/response/ValidateCode';
import { AttributesState } from '@/app/shared/types/stores/attribute.state.interface';
import { PaginatedResponse } from '@/app/shared/types/api/api-response.interface';
import { HistoryData } from '@/app/shared/types/history-data.interface';
import { Attribute } from '@/app/shared/types/models/attribute.interface';
import { OverviewDataEvent } from '@/app/shared/types/events/overview-data-event.interface';
import { DetailPage } from '@/app/shared/types/models/detail.page.interface';
import { EntityReferencesDependencies } from '@/app/shared/types/entity-references-dependencies.interface';

/**todo: check if we can get lockDurationSeconds and other similar defaults while logins */
export const useAttributesStore = defineStore({
  id: 'attributeStoreId',
  state: () =>
    ({
      attributeMetadataList: {} as PaginatedResponse<Attribute>,
      historyData: {} as PaginatedResponse<HistoryData>,
      massDeleteDependencies: [] as Array<EntityReferencesDependencies>,
      whereUsedData: [],
      attributeDetailsMap: new Map(),
      allAttributesSystem: [],
      deleteEntityIds: {} as Array<number>,
      tabs: [{ id: 0, name: 'All Attributes' }],
      selectedTab: 0,
      attribute: {} as AttributeResponse,
      lockDurationSeconds: 900,
      requestParams: {
        codeType: CodeType.ATTRIBUTE,
      },
      pagination: {
        page: 0,
        size: 10,
      },
      attributeValidate: {} as ValidateCode,
    } as AttributesState),
  // TODO: Getter should return computed or processed values, all this functions
  // return the same value as accessing the state directly
  getters: {
    getAttributeById(state): any {
      return (id: number) => state.attributeDetailsMap?.get(id);
    },
    getAttributeMetadataList(): any {
      return this.attributeMetadataList;
    },
    getAutoLockStatusById(state): any {
      return (id: number) => state.tabs.find((tab) => tab.id === id);
    },
  },

  actions: {
    async fetchAttributeMetaData(clientId: number, params: PaginationParams) {
      const {
        data: { data },
      } = await attributesApi.getAttributes(clientId, params);
      this.attributeMetadataList = data;
    },
    async fetchSystemAllAttributes(
      clientId: number,
      { page = 0, size, codeType = CodeType.ATTRIBUTE }: PaginationParams
    ) {
      const {
        data: { data },
      } = await attributesApi.getAttributes(clientId, {
        page,
        codeType,
      });
      this.allAttributesSystem = data || [];
    },
    async fetchHistoryDataById(
      clientId: number,
      attributeId: number,
      queryParams: PaginationParams
    ) {
      const {
        data: { data },
      } = await attributesApi.getHistoryByAttributeId(
        clientId,
        attributeId,
        queryParams
      );
      this.historyData = data || {};
    },
    async fetchAttributeById(clientId: number, attributeId: number) {
      const {
        data: { data },
      } = await attributesApi.getAttributeById(clientId, attributeId);
      this.attribute = data;
    },
    /**refactor both methods to one */
    async createAttributeTab(
      clientId: number,
      attributeId: number,
      attributeName: string,
      isNew: boolean,
      dirtyTab: Map<string, boolean>
    ) {
      //if not newly created attribute, fetch details from api(async call)
      if (!isNew) {
        const {
          data: { data },
        } = await attributesApi.getAttributeById(clientId, attributeId);
        this.attributeDetailsMap.set(attributeId, {
          id: attributeId,
          name: attributeName,
          isNew: isNew,
          dirtyTab: dirtyTab,
          isCopied: {
            id: -1,
            isCopy: false,
          },
          overviewData: data,
        });
      }
      //if tab is already opened, switch to that tab
      const tabIndex = this.tabs.findIndex((tab) => tab.name === attributeName);
      if (tabIndex === -1) {
        this.tabs.push({
          id: attributeId,
          name: attributeName,
          autoLocked: true,
        });
        this.selectedTab = this.tabs.length - 1;
      } else {
        this.selectedTab = tabIndex;
      }
    },
    async updateAttribute(
      clientId: number,
      attributeId: number | string,
      attribute: OverviewDataEvent
    ) {
      const {
        data: { data },
      } = await attributesApi.updateAttribute(clientId, attributeId, attribute);
      const index = this.tabs.findIndex((tab) => tab.id === attributeId);
      this.tabs[index].name = attribute.shortName;
      this.attribute = data;
      this.attributeDetailsMap.get(attributeId).overviewData = this.attribute;
    },

    async createNewAttributeTab(
      clientId: number,
      detailPage: DetailPage,
      // TODO: Create an interface for this attribute or use an existing one
      defaultData: any
    ) {
      if (detailPage.isNew) {
        detailPage.overviewData = defaultData;
        this.attributeDetailsMap.set(detailPage.id, detailPage);
      } else {
        const { data } = await attributesApi.getAttributeById(
          clientId,
          detailPage.id
        );
        detailPage.overviewData = data;
        this.attributeDetailsMap.set(detailPage.id, detailPage);
      }
      //if tab is already opened, switch to that tab

      this.tabs.push({
        id: detailPage.id,
        name: detailPage.name,
        autoLocked: false,
      });
      this.selectedTab = this.tabs.length - 1;
    },
    setTabDirtyFieldById(
      attributeId: string | number,
      tabName: string,
      isDirty: boolean
    ) {
      this.attributeDetailsMap
        .get(attributeId)
        ?.dirtyTab?.set(tabName, isDirty);
    },
    getTabDirtyStatus(attributeId: string | number, tabName: string) {
      return this.attributeDetailsMap.get(attributeId)?.dirtyTab?.get(tabName);
    },
    setTabNewFieldById(attributeId: string | number, value: boolean) {
      const data = this.attributeDetailsMap.get(attributeId);
      data.isNew = value;
    },
    setTabIsCopyStatusToDefaultById(attributeId: string | number) {
      const data = this.attributeDetailsMap.get(attributeId);
      data.isCopied.id = -1;
      data.isCopied.isCopy = false;
    },
    // TODO: Create an interface for this attribute or use an existing one
    setOverviewDataById(attributeId: string | number, value: any) {
      const data = this.attributeDetailsMap.get(attributeId);
      data.overviewData = value;
    },
    removeTab(attributeId: number) {
      // TODO: Create an interface for this attribute or use an existing one
      const fIndex = this.tabs.findIndex((tab: any) => tab.id === attributeId);
      fIndex > 0 && this.tabs.splice(fIndex, 1);
    },
    async fetchWhereUsedDetails(
      clientId: number,
      attributeId: number | string
    ) {
      const alertStore = useAlertStore();
      try {
        const {
          data: { data },
        } = await attributesApi.getWhereUsedByAttributeId(
          clientId,
          attributeId
        );
        this.whereUsedData = data || {};
      } catch (e) {
        const error: any = e;
        alertStore.showNotification(
          error.errors[0].message,
          'Filters',
          Motif.ERROR
        );
      }
    },
    saveCreatedAttribute(oldId: string | number, data: Attribute) {
      this.setAttributeDetails(oldId, data);
      this.updateTabs(oldId, data.id, data.shortName);
    },
    updateTabs(oldId: string | number, id: string | number, shortName: string) {
      const fIndex = this.tabs.findIndex((elem) => elem.id === oldId);
      this.tabs[fIndex].id = fIndex > 0 ? id : -1;
      this.tabs[fIndex].name = fIndex > 0 ? shortName : '';
    },
    setAttributeDetails(oldId: string | number, data: Attribute) {
      const { id, shortName } = data;
      this.attributeDetailsMap.delete(oldId);
      this.attributeDetailsMap.set(id, {
        id,
        name: shortName,
        isNew: false,
        dirtyTab: new Map<string, boolean>(),
        isCopied: {
          id: -1,
          isCopy: false,
        },
        overviewData: data,
      });
    },
    deleteAttribute(attributeId: number) {
      this.attributeDetailsMap.delete(attributeId);
      this.removeTab(attributeId);
    },
    async copyAttribute(
      attribute: OverviewDataEvent,
      clientId: number,
      attributeId: number | string
    ) {
      const resp = await attributesApi.copyAttributeById(
        attribute,
        clientId,
        attributeId
      );
      this.attribute = resp?.data?.data;
    },
    updateAutoLockStatusForTab(attributeId: number, autoLockValue: boolean) {
      const fIndex = this.tabs.findIndex((elem) => elem.id === attributeId);
      if (fIndex !== -1) {
        this.tabs[fIndex].autoLocked = autoLockValue;
      }
    },
    updateAutoLockInfoInOverviewList(
      clientId: number,
      attributeId: number,
      autoLockedByUser: string | null,
      autoLockExpiry: number | null
    ) {
      const fIndex = this.attributeMetadataList.content.findIndex(
        (elem: Attribute) =>
          elem.id === attributeId && elem.clientId === clientId
      );
      if (fIndex !== -1) {
        const data =
          this.attributeMetadataList?.content?.[fIndex]?.versionControls?.[0];
        if (data) {
          data.autoLockedByUserId = autoLockedByUser;
          data.autoLockExpiry = autoLockExpiry;
          data.lockedByUsername = autoLockedByUser;
        }
      }
    },
    async validateAttributeCode(
      clientId: number,
      attributeId: number,
      dataSourceName: string,
      file: FormData
    ) {
      const response = await attributesApi.validateAttributeCode(
        clientId,
        attributeId,
        dataSourceName,
        file
      );
      this.attributeValidate = response.data.data;
    },
    updateCodeInVersionControlsById(attributeId: number, code: string) {
      const versionControls =
        this.attributeDetailsMap.get(attributeId).overviewData.versionControls;
      versionControls[0].code = code;
    },
    updateRequestParams(params: SearchParams) {
      Object.keys(params).forEach((key) => {
        this.requestParams[key as keyof SearchParams] =
          params[key as keyof SearchParams];
      });
    },
    async rollbackAttributeCode(clientId: number, rollbackRevisionId: number) {
      await attributesApi.rollbackAttributeCode(clientId, rollbackRevisionId);
    },
    async saveAttributeCode(
      clientId: number,
      attributeId: number,
      payload: CheckCodeSyntaxPayload
    ) {
      await attributesApi.saveAttributeCode(clientId, attributeId, payload);
    },
    async createAttribute(clientId: number, attribute: AttributeResponse) {
      const created = await attributesApi.createAttribute(clientId, attribute);
      this.attribute = created.data.data;
    },
    async checkCodeSyntax(clientId: number, code: CheckCodeSyntaxPayload) {
      await attributesApi.checkCodeSyntax(clientId, code);
    },
    async deleteAttributes(
      clientId: number,
      selectedAttributes?: Array<number>
    ) {
      await attributesApi.deleteAttributes(clientId, selectedAttributes);
    },
    async checkDuplicateShortName(clientId: number, name: string) {
      await attributesApi.checkDuplicateShortName(clientId, name);
    },
    async lockAllAttributes(clientId: number) {
      await attributesApi.lockAllAttributes(clientId);
    },
    async unlockAllAttributes(clientId: number) {
      await attributesApi.unlockAllAttributes(clientId);
    },
    async lockAttribute(
      clientId: number,
      attributteId: number,
      isAutoLock: boolean
    ) {
      await attributesApi.lockAttribute(clientId, attributteId, isAutoLock);
    },
    async retrieveCyclicDependencies(
      clientId: number,
      selectedAttributes?: Array<number>
    ) {
      const {
        data: {
          data: { entities, ids },
        },
      } = await attributesApi.retrieveCyclicDependencies(
        clientId,
        selectedAttributes
      );
      this.massDeleteDependencies = entities;
      this.deleteEntityIds = ids;
    },
  },
});
